import { toast } from 'react-hot-toast';

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios';
import i18n from 'i18n';
import type { Dispatch } from 'react';

import type { Middleware } from '@reduxjs/toolkit';

import { LoginState, logout } from 'Auth/data';
import { urls } from 'Router';
import { isNotProductionEnvironment } from 'Utils/environments';

let currentStore: any = null;
let axiosInterceptor: number | null = null;
let apiClientSetup = false;
let interceptorsInitialized = false;

export const axiosMiddleware: Middleware = store => {
  if (!interceptorsInitialized) {
    currentStore = store;
    setupInterceptors(store);
    setupApiClientInterceptors(store);
    interceptorsInitialized = true;
  }

  return (next: Dispatch<any>) => (action: any) => {
    if (currentStore !== store) {
      currentStore = store;
      setupInterceptors(store);
    }

    return next(action);
  };
};

const setupInterceptors = (store: any) => {
  if (!store) {
    return;
  }

  if (axiosInterceptor !== null) {
    axios.interceptors.response.eject(axiosInterceptor);
  }

  axiosInterceptor = axios.interceptors.response.use(onResponse, onResponseError(store));
};

const setupApiClientInterceptors = async (store: any) => {
  if (!store || apiClientSetup) {
    return;
  }

  try {
    const { getApiClient } = await import('Api/api-client');
    await getApiClient().setupStoreInterceptors(store);
    apiClientSetup = true;
  } catch (error) {
    console.warn('Failed to setup API client store interceptors:', error);
  }
};

export const setupInstanceInterceptors = (
  axiosInstance: AxiosInstance,
  store?: any
): AxiosInstance => {
  axiosInstance.interceptors.request.use(onRequest, onRequestError);

  if (store) {
    axiosInstance.interceptors.response.use(onResponse, onResponseError(store));
  } else {
    axiosInstance.interceptors.response.use(onResponse, onResponseErrorDefault);
  }

  return axiosInstance;
};

export const getCurrentStore = () => currentStore;

const doNothing = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => config;

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (isNotProductionEnvironment()) {
    console.debug(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
  }

  const currentLoginState = currentStore?.getState().auth?.loginState;
  const isConfigRequest = config.url?.toLowerCase().includes('/api/config');
  const isAuthRequest = config.url?.toLowerCase().includes('/api/auth/');
  const isCourseJoinStatusRequest = config.url?.toLowerCase().includes('/api/courses/join/status');

  if (
    (currentLoginState === LoginState.LOGGING_OUT || currentLoginState === LoginState.NOT_LOGGED) &&
    !isConfigRequest &&
    !isAuthRequest &&
    !isCourseJoinStatusRequest
  ) {
    const controller = new AbortController();
    controller.abort();
    config.signal = controller.signal;
  }

  return doNothing(config);
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  if (isNotProductionEnvironment()) {
    console.error('[API Request Error]', error);
  }

  return Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  if (isNotProductionEnvironment()) {
    console.debug(`[API Response] ${response.status} ${response.config.url}`);
  }

  return response;
};

const onResponseError = (store: any) => async (error: AxiosError) => {
  const response = (error.response as AxiosResponse) || error;

  if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
    return Promise.reject(error);
  }

  if (
    error.config?.url?.toLowerCase().includes('auth/') ||
    error.config?.url?.toLowerCase().includes('logout')
  ) {
    return Promise.reject(error);
  }

  const currentLoginState = store.getState().auth?.loginState;

  if (
    response?.status === 401 &&
    currentLoginState !== LoginState.LOGGING_OUT &&
    currentLoginState !== LoginState.NOT_LOGGED
  ) {
    // 401 means the session is invalid/expired and backend could not refresh it.
    // We should log the user out.
    store.dispatch(logout());
  } else if (response?.status === 403) {
    const errorMessage =
      response?.data?.message ?? response?.data?.errorTranslationCode ?? 'Access denied';
    toast.error(i18n.t(errorMessage));
    window.location.href = `${window.location.protocol}//${window.location.host}${urls.forbiddenPage}`;
  } else {
    handleException(response);
  }

  if (isNotProductionEnvironment()) {
    console.error('[API Response Error]', response);
  }

  return Promise.reject(error);
};

const onResponseErrorDefault = (error: AxiosError): Promise<AxiosError> => {
  const response = (error.response as AxiosResponse) || error;

  if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
    return Promise.reject(error);
  }

  if (
    error.config?.url?.toLowerCase().includes('auth/') ||
    error.config?.url?.toLowerCase().includes('logout')
  ) {
    return Promise.reject(error);
  }

  if (response?.status !== 401 && response?.status !== 403) {
    handleException(response);
  }

  if (isNotProductionEnvironment()) {
    console.error('[API Response Error (Default)]', response);
  }

  return Promise.reject(error);
};

const handleException = (response: AxiosResponse): void => {
  const errorData = response?.data;

  let errorMessage: string;

  if (errorData?.message) {
    errorMessage = errorData.message;
  } else if (errorData?.errorTranslationCode) {
    errorMessage = errorData.errorTranslationCode;
  } else if (errorData?.errors) {
    if (Array.isArray(errorData.errors)) {
      errorMessage = errorData.errors.join(', ');
    } else {
      errorMessage = JSON.stringify(errorData.errors);
    }
  } else {
    errorMessage = 'An unexpected error occurred';
  }

  if (errorMessage === 'Unexpected error in retry handler') {
    return;
  }

  try {
    const translatedMessage = i18n.t(errorMessage);

    if (translatedMessage === errorMessage && !i18n.exists(errorMessage)) {
      toast.error(errorMessage);
    } else {
      toast.error(translatedMessage);
    }
  } catch {
    toast.error(errorMessage);
  }
};
